export interface ParsedTable {
  columns: string[];
  rows: string[][];
}

const splitRow = (line: string): string[] => {
  let s = line.trim();
  if (s.startsWith("|")) s = s.slice(1);
  if (s.endsWith("|")) s = s.slice(0, -1);
  // Split on unescaped pipes.
  return s.split(/(?<!\\)\|/).map((c) => c.replace(/\\\|/g, "|").trim());
};

const isSeparator = (line: string): boolean =>
  /^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)*\|?\s*$/.test(line);

/**
 * Extracts the FIRST GFM markdown table found in `markdown` into columns + rows.
 * Returns null when no well-formed table (header + separator + at least one data row) is present.
 */
export const parseFirstMarkdownTable = (markdown: string): ParsedTable | null => {
  if (!markdown) return null;
  const lines = markdown.split(/\r?\n/);

  for (let i = 0; i < lines.length - 1; i++) {
    const header = lines[i];
    const sep = lines[i + 1];
    if (header.includes("|") && isSeparator(sep)) {
      const columns = splitRow(header);
      const rows: string[][] = [];
      for (let j = i + 2; j < lines.length; j++) {
        const line = lines[j];
        if (!line.includes("|") || line.trim() === "") break;
        const cells = splitRow(line);
        // Normalize ragged rows to the column count.
        while (cells.length < columns.length) cells.push("");
        rows.push(cells.slice(0, columns.length));
      }
      if (columns.length > 0 && rows.length > 0) {
        return { columns, rows };
      }
    }
  }
  return null;
};

/** Parses a table cell into a number, stripping currency/grouping/percent decoration. Returns NaN if not numeric. */
export const toNumber = (cell: string): number => {
  if (cell == null) return NaN;
  const cleaned = cell.replace(/[$,%\s]/g, "").replace(/[()]/g, (m) => (m === "(" ? "-" : ""));
  if (cleaned === "" || cleaned === "-") return NaN;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : NaN;
};
